import React, { useState, useEffect, useMemo, useCallback } from 'react';
import TitleAndInfo from './TitleAndInfo';
import {
    Typography, Box, Container, IconButton, Paper, useTheme,
    CircularProgress, Chip, useMediaQuery, TextField, InputAdornment,
    MenuItem, Checkbox, Tooltip, Collapse, Badge, Divider, Button, Dialog, Popover,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
    ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon,
    TrendingUp as TrendingUpIcon, TrendingDown as TrendingDownIcon,
    Search as SearchIcon, FilterList as FilterListIcon,
    Today as TodayIcon, Close as CloseIcon, AccessTime as AccessTimeIcon,
    KeyboardArrowUp as KeyboardArrowUpIcon, KeyboardArrowDown as KeyboardArrowDownIcon,
    CalendarMonth as CalendarMonthIcon,
} from '@mui/icons-material';
import axios from '../config/axiosConfig';

// ─── Constants & Helpers ────────────────────────────────────────────────────────

const IMPACT_LEVELS = ['High', 'Medium', 'Low'];

const formatDateKey = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formatDate = (date) => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}`;
};

const formatMobileDate = (date) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}`;
};

const formatTime = (dateString) =>
    new Date(dateString).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });

const formatValue = (value, unit) => {
    if (value === null || value === undefined) return '-';
    return `${value}${unit ? ' ' + unit : ''}`;
};

const getNextValidDate = (date, direction) => {
    const newDate = new Date(date);
    do { newDate.setDate(newDate.getDate() + direction); }
    while (newDate.getDay() === 0 || newDate.getDay() === 6);
    return newDate;
};

const getWeekDates = (date) => {
    const dates = [];
    const currentDate = new Date(date);
    while (currentDate.getDay() !== 1) currentDate.setDate(currentDate.getDate() - 1);
    for (let i = 0; i < 5; i++) {
        const newDate = new Date(currentDate);
        dates.push({ date: newDate, formatted: formatDate(newDate), fullDate: formatDateKey(newDate) });
        currentDate.setDate(currentDate.getDate() + 1);
    }
    return dates;
};

const getSurprise = (actual, estimate) => {
    if (actual === null || actual === undefined || estimate === null || estimate === undefined) return null;
    return +(actual - estimate).toFixed(4);
};

const isTodayDate = (date) => formatDateKey(new Date()) === formatDateKey(date);

const getTimeUntil = (dateString) => {
    const diff = new Date(dateString) - new Date();
    if (diff <= 0) return null;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 24) return `${Math.floor(hours / 24)}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
};

const getImpactColor = (impact, theme) => {
    if (impact === 'High') return theme.palette.error.main;
    if (impact === 'Medium') return theme.palette.warning.main;
    return theme.palette.success.main;
};

// ─── Impact Dots ────────────────────────────────────────────────────────────────

const ImpactDots = ({ impact, theme }) => {
    const filled = impact === 'High' ? 3 : impact === 'Medium' ? 2 : 1;
    const color = getImpactColor(impact, theme);
    return (
        <Tooltip title={`${impact} Impact`} arrow>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                {[1, 2, 3].map((i) => (
                    <Box key={i} sx={{
                        width: 8, height: 8, borderRadius: '50%',
                        backgroundColor: i <= filled ? color : theme.palette.action.disabled,
                    }} />
                ))}
                <Typography variant="caption" sx={{ ml: 0.5, color, fontWeight: 600, fontSize: '0.7rem' }}>
                    {impact}
                </Typography>
            </Box>
        </Tooltip>
    );
};

// ─── Surprise Chip ──────────────────────────────────────────────────────────────

const SurpriseChip = ({ actual, estimate, unit, theme }) => {
    const surprise = getSurprise(actual, estimate);
    if (surprise === null) return <Typography variant="body2" color="text.secondary">-</Typography>;

    const isBeat = surprise > 0;
    const isMiss = surprise < 0;
    const label = surprise === 0
        ? 'In-line'
        : `${isBeat ? '+' : ''}${surprise}${unit ? ' ' + unit : ''}`;
    const color = isBeat
        ? theme.palette.success.main
        : isMiss
            ? theme.palette.error.main
            : theme.palette.text.secondary;
    const bgColor = isBeat
        ? `${theme.palette.success.main}18`
        : isMiss
            ? `${theme.palette.error.main}18`
            : `${theme.palette.text.secondary}12`;

    return (
        <Chip label={label} size="small" sx={{
            backgroundColor: bgColor, color, fontWeight: 600,
            fontSize: '0.75rem', height: 24, border: `1px solid ${color}30`,
        }} />
    );
};

// ─── Countdown Chip ─────────────────────────────────────────────────────────────

const CountdownChip = ({ dateString }) => {
    const [timeLeft, setTimeLeft] = useState(getTimeUntil(dateString));

    useEffect(() => {
        const interval = setInterval(() => setTimeLeft(getTimeUntil(dateString)), 60000);
        return () => clearInterval(interval);
    }, [dateString]);

    if (!timeLeft) return null;

    return (
        <Chip
            icon={<AccessTimeIcon sx={{ fontSize: '0.85rem !important' }} />}
            label={timeLeft} size="small" variant="outlined"
            sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, ml: 1, '& .MuiChip-icon': { ml: '4px' } }}
        />
    );
};

// ─── Detail Item (mobile expanded) ──────────────────────────────────────────────

const DetailItem = ({ label, value, color }) => (
    <Box>
        <Typography variant="caption" color="text.secondary"
            sx={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, color: color || 'text.primary' }}>
            {value}
        </Typography>
    </Box>
);

// ─── Mobile Event Card ──────────────────────────────────────────────────────────

const MobileEventCard = ({ event, theme }) => {
    const [expanded, setExpanded] = useState(false);
    const isUpcoming = event.actual === null || event.actual === undefined;
    const surprise = getSurprise(event.actual, event.estimate);
    const impactColor = getImpactColor(event.impact, theme);

    return (
        <Paper variant="stats" sx={{
            mb: 1.5, overflow: 'hidden', opacity: isUpcoming ? 0.75 : 1,
            borderLeft: `3px solid ${impactColor}`,
        }}>
            <Box onClick={() => setExpanded(!expanded)}
                sx={{ p: 1.5, cursor: 'pointer', '&:active': { backgroundColor: 'action.hover' } }}>
                {/* Row 1: Time + Country + Impact */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            {formatTime(event.date)}
                        </Typography>
                        {isUpcoming && <CountdownChip dateString={event.date} />}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label={event.country} size="small"
                            sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }} />
                        <ImpactDots impact={event.impact} theme={theme} />
                    </Box>
                </Box>

                {/* Row 2: Event name */}
                <Typography variant="body2" sx={{
                    fontWeight: 600, mb: 0.5,
                    fontStyle: isUpcoming ? 'italic' : 'normal',
                }}>
                    {event.event}
                    {isUpcoming && (
                        <Typography component="span" variant="caption"
                            sx={{ ml: 1, color: 'text.secondary', fontStyle: 'normal' }}>
                            (Pending)
                        </Typography>
                    )}
                </Typography>

                {/* Row 3: Quick stats */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    {!isUpcoming && (
                        <Typography variant="caption">
                            <b>Actual:</b> {formatValue(event.actual, event.unit)}
                        </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                        <b>Est:</b> {formatValue(event.estimate, event.unit)}
                    </Typography>
                    {!isUpcoming && (
                        <SurpriseChip actual={event.actual} estimate={event.estimate}
                            unit={event.unit} theme={theme} />
                    )}
                    <Box sx={{ ml: 'auto' }}>
                        {expanded
                            ? <KeyboardArrowUpIcon fontSize="small" color="action" />
                            : <KeyboardArrowDownIcon fontSize="small" color="action" />}
                    </Box>
                </Box>
            </Box>

            {/* Expanded details */}
            <Collapse in={expanded}>
                <Divider />
                <Box sx={{ p: 1.5, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                    <DetailItem label="Previous" value={formatValue(event.previous, event.unit)} />
                    <DetailItem label="Estimate" value={formatValue(event.estimate, event.unit)} />
                    <DetailItem label="Actual" value={formatValue(event.actual, event.unit)} />
                    <DetailItem label="Surprise"
                        value={surprise !== null
                            ? `${surprise > 0 ? '+' : ''}${surprise}${event.unit ? ' ' + event.unit : ''}`
                            : '-'}
                        color={surprise > 0 ? theme.palette.success.main
                            : surprise < 0 ? theme.palette.error.main : undefined} />
                    <DetailItem label="Change"
                        value={event.change !== null && event.change !== undefined
                            ? `${event.change > 0 ? '+' : ''}${event.change}${event.changePercentage ? ` (${event.changePercentage}%)` : ''}`
                            : '-'}
                        color={event.change > 0 ? theme.palette.success.main
                            : event.change < 0 ? theme.palette.error.main : undefined} />
                    <DetailItem label="Currency" value={event.currency} />
                </Box>
            </Collapse>
        </Paper>
    );
};

// ─── Date Picker Dialog ─────────────────────────────────────────────────────────

const DatePickerDialog = ({ open, onClose, onSelect, selectedDate, earliestDate, latestDate, theme }) => {
    const [viewMonth, setViewMonth] = useState(new Date(selectedDate));
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'];

    const year = viewMonth.getFullYear();
    const month = viewMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const handleSelect = (day) => {
        const newDate = new Date(year, month, day);
        if (newDate.getDay() === 0 || newDate.getDay() === 6) return;
        if (earliestDate && newDate < new Date(earliestDate)) return;
        if (latestDate && newDate > new Date(latestDate)) return;
        onSelect(newDate);
        onClose();
    };

    const changeMonth = (dir) => {
        setViewMonth(new Date(year, month + dir, 1));
    };

    return (
        <Dialog open={open} onClose={onClose} PaperProps={{
            sx: {
                borderRadius: 3, p: 2, minWidth: 320,
                backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(19, 47, 76, 0.95)' : 'rgba(255,255,255,0.98)',
                backdropFilter: 'blur(20px)',
            }
        }}>
            {/* Month navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <IconButton size="small" onClick={() => changeMonth(-1)}><ChevronLeftIcon /></IconButton>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {monthNames[month]} {year}
                </Typography>
                <IconButton size="small" onClick={() => changeMonth(1)}><ChevronRightIcon /></IconButton>
            </Box>

            {/* Day-of-week headers */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 1 }}>
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
                    <Typography key={d} variant="caption" sx={{
                        textAlign: 'center', fontWeight: 700, color: 'text.secondary', fontSize: '0.7rem'
                    }}>{d}</Typography>
                ))}
            </Box>

            {/* Days grid */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                {Array.from({ length: firstDay }, (_, i) => <Box key={`e-${i}`} />)}
                {Array.from({ length: daysInMonth }, (_, i) => {
                    const day = i + 1;
                    const date = new Date(year, month, day);
                    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                    const isSel = formatDateKey(date) === formatDateKey(selectedDate);
                    const isToday = isTodayDate(date);
                    const isDisabled = isWeekend
                        || (earliestDate && date < new Date(earliestDate))
                        || (latestDate && date > new Date(latestDate));

                    return (
                        <Box key={day} onClick={() => !isDisabled && handleSelect(day)} sx={{
                            width: 36, height: 36, display: 'flex', alignItems: 'center',
                            justifyContent: 'center', borderRadius: '50%', fontSize: '0.85rem',
                            cursor: isDisabled ? 'default' : 'pointer',
                            fontWeight: (isSel || isToday) ? 700 : 400,
                            color: isDisabled ? 'text.disabled' : isSel ? 'primary.contrastText' : 'text.primary',
                            backgroundColor: isSel ? 'primary.main' : 'transparent',
                            border: isToday && !isSel
                                ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                            transition: 'all 0.15s',
                            '&:hover': !isDisabled ? { backgroundColor: isSel ? 'primary.dark' : 'action.hover' } : {},
                        }}>{day}</Box>
                    );
                })}
            </Box>

            {/* Go to today */}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Button size="small" startIcon={<TodayIcon />} onClick={() => {
                    const today = new Date();
                    onSelect(today.getDay() === 0 || today.getDay() === 6 ? getNextValidDate(today, -1) : today);
                    onClose();
                }} sx={{ textTransform: 'none', fontWeight: 600 }}>Go to Today</Button>
            </Box>
        </Dialog>
    );
};

// ─── Country Filter with Search ─────────────────────────────────────────────────

const CountryFilter = ({ selectedCountries, setSelectedCountries, availableCountries, isMobile }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [countrySearch, setCountrySearch] = useState('');
    const open = Boolean(anchorEl);

    const filtered = availableCountries.filter((c) =>
        c.toLowerCase().includes(countrySearch.toLowerCase())
    );

    const handleToggle = (country) => {
        setSelectedCountries((prev) =>
            prev.includes(country) ? prev.filter((c) => c !== country) : [...prev, country]
        );
    };

    return (
        <>
            <Button
                size="small"
                variant="outlined"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{
                    borderRadius: 2, textTransform: 'none', fontSize: '0.875rem',
                    minWidth: isMobile ? '100%' : 140, justifyContent: 'space-between',
                    color: 'text.primary', borderColor: 'divider',
                    height: 40, px: 1.5,
                }}
            >
                {selectedCountries.length > 0
                    ? `Country (${selectedCountries.length})`
                    : 'Country'}
                <KeyboardArrowDownIcon fontSize="small" sx={{ ml: 0.5 }} />
            </Button>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => { setAnchorEl(null); setCountrySearch(''); }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                PaperProps={{
                    sx: {
                        mt: 0.5, borderRadius: 2, width: 220,
                        maxHeight: 340, display: 'flex', flexDirection: 'column',
                    }
                }}
            >
                {/* Search input */}
                <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
                    <TextField
                        size="small" fullWidth autoFocus
                        placeholder="Search countries..."
                        value={countrySearch}
                        onChange={(e) => setCountrySearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.8rem', height: 32 } }}
                    />
                </Box>

                {/* Country list */}
                <Box sx={{ overflow: 'auto', flex: 1 }}>
                    {filtered.length === 0 ? (
                        <Typography variant="caption" color="text.secondary"
                            sx={{ p: 1.5, display: 'block', textAlign: 'center' }}>
                            No matches
                        </Typography>
                    ) : (
                        filtered.map((country) => (
                            <MenuItem key={country} onClick={() => handleToggle(country)}
                                dense sx={{ py: 0.25, px: 1, minHeight: 32 }}>
                                <Checkbox
                                    checked={selectedCountries.includes(country)}
                                    size="small"
                                    sx={{ p: 0.25, mr: 0.75 }}
                                />
                                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                    {country}
                                </Typography>
                            </MenuItem>
                        ))
                    )}
                </Box>

                {/* Quick actions */}
                {selectedCountries.length > 0 && (
                    <Box sx={{ p: 0.75, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end' }}>
                        <Button size="small" onClick={() => setSelectedCountries([])}
                            sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}>
                            Clear all
                        </Button>
                    </Box>
                )}
            </Popover>
        </>
    );
};

// ─── Impact Filter ──────────────────────────────────────────────────────────────

const ImpactFilter = ({ selectedImpacts, setSelectedImpacts, isMobile, theme }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleToggle = (level) => {
        setSelectedImpacts((prev) =>
            prev.includes(level) ? prev.filter((l) => l !== level) : [...prev, level]
        );
    };

    return (
        <>
            <Button size="small" variant="outlined"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{
                    borderRadius: 2, textTransform: 'none', fontSize: '0.875rem',
                    minWidth: isMobile ? '100%' : 130, justifyContent: 'space-between',
                    color: 'text.primary', borderColor: 'divider', height: 40, px: 1.5,
                }}>
                {selectedImpacts.length > 0 ? `Impact (${selectedImpacts.length})` : 'Impact'}
                <KeyboardArrowDownIcon fontSize="small" sx={{ ml: 0.5 }} />
            </Button>
            <Popover open={open} anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                PaperProps={{ sx: { mt: 0.5, borderRadius: 2, width: 180 } }}>
                {IMPACT_LEVELS.map((level) => (
                    <MenuItem key={level} onClick={() => handleToggle(level)}
                        dense sx={{ py: 0.25, px: 1, minHeight: 32 }}>
                        <Checkbox checked={selectedImpacts.includes(level)}
                            size="small" sx={{ p: 0.25, mr: 0.75 }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                            {[1, 2, 3].map((i) => (
                                <Box key={i} sx={{
                                    width: 7, height: 7, borderRadius: '50%',
                                    backgroundColor: i <= (level === 'High' ? 3 : level === 'Medium' ? 2 : 1)
                                        ? getImpactColor(level, theme)
                                        : theme.palette.action.disabled,
                                }} />
                            ))}
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', ml: 0.5 }}>
                                {level}
                            </Typography>
                        </Box>
                    </MenuItem>
                ))}
                {selectedImpacts.length > 0 && (
                    <Box sx={{ p: 0.75, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end' }}>
                        <Button size="small" onClick={() => setSelectedImpacts([])}
                            sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}>
                            Clear all
                        </Button>
                    </Box>
                )}
            </Popover>
        </>
    );
};

// ─── Filter Bar ─────────────────────────────────────────────────────────────────

const FilterBar = ({
    searchQuery, setSearchQuery, selectedImpacts, setSelectedImpacts,
    selectedCountries, setSelectedCountries, availableCountries,
    onClearFilters, hasActiveFilters, isMobile, theme,
}) => {
    const [showFilters, setShowFilters] = useState(false);

    const filterContent = (
        <Box sx={{
            display: 'flex', alignItems: isMobile ? 'stretch' : 'center',
            flexDirection: isMobile ? 'column' : 'row', gap: 1.5,
            p: isMobile ? 2 : 0, flexWrap: 'wrap',
        }}>
            {/* Search */}
            <TextField size="small" placeholder="Search events..." value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                    ),
                    ...(searchQuery && {
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setSearchQuery('')}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }),
                }}
                sx={{
                    minWidth: isMobile ? '100%' : 220,
                    '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.875rem' },
                }}
            />

            {/* Impact filter */}
            <ImpactFilter
                selectedImpacts={selectedImpacts}
                setSelectedImpacts={setSelectedImpacts}
                isMobile={isMobile}
                theme={theme}
            />

            {/* Country filter */}
            <CountryFilter
                selectedCountries={selectedCountries}
                setSelectedCountries={setSelectedCountries}
                availableCountries={availableCountries}
                isMobile={isMobile}
            />

            {/* Clear */}
            {hasActiveFilters && (
                <Button size="small" onClick={onClearFilters}
                    startIcon={<CloseIcon fontSize="small" />}
                    sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    Clear
                </Button>
            )}
        </Box>
    );

    if (isMobile) {
        return (
            <Box>
                <Box sx={{ px: 1.5, pt: 1.25, pb: 0.5 }}>
                    <TextField size="small" fullWidth placeholder="Search events..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} /></InputAdornment>, ...(searchQuery && { endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => setSearchQuery('')}><CloseIcon fontSize="small" /></IconButton></InputAdornment> }) }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.875rem' } }} />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1.5, py: 0.75 }}>
                    <Badge color="primary" variant="dot" invisible={!(selectedImpacts.length > 0 || selectedCountries.length > 0)}>
                        <IconButton size="small" onClick={() => setShowFilters(!showFilters)}
                            sx={{ border: 1, borderColor: 'divider', borderRadius: 2, px: 1.5 }}>
                            <FilterListIcon fontSize="small" />
                            <Typography variant="caption" sx={{ ml: 0.5, fontWeight: 600 }}>Filters</Typography>
                        </IconButton>
                    </Badge>
                    {hasActiveFilters && <Button size="small" onClick={onClearFilters} startIcon={<CloseIcon fontSize="small" />} sx={{ ml: 1, textTransform: 'none', fontWeight: 600 }}>Clear</Button>}
                </Box>
                <Collapse in={showFilters}>{filterContent}</Collapse>
            </Box>
        );
    }

    return <Box sx={{ px: 2, py: 1.5 }}>{filterContent}</Box>;
};

// ─── Empty State ────────────────────────────────────────────────────────────────

const EmptyState = ({ message }) => (
    <Box sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', py: 8, px: 2,
    }}>
        <CalendarMonthIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
            {message}
        </Typography>
        <Typography variant="body2" color="text.disabled">
            Try selecting a different date or adjusting your filters.
        </Typography>
    </Box>
);

// ─── Main Component ─────────────────────────────────────────────────────────────

const EconomicCalendar = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [selectedDate, setSelectedDate] = useState(new Date());
    const [weekDates, setWeekDates] = useState([]);
    const [economicData, setEconomicData] = useState({});
    const [loading, setLoading] = useState(true);
    const [latestDate, setLatestDate] = useState(null);
    const [earliestDate, setEarliestDate] = useState(null);

    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedImpacts, setSelectedImpacts] = useState([]);
    const [selectedCountries, setSelectedCountries] = useState([]);

    // Date picker
    const [datePickerOpen, setDatePickerOpen] = useState(false);

    const titleTop = 'Economic Calendar: ';
    const title = '';
    const info = '';

    // ── Week dates ──
    useEffect(() => {
        setWeekDates(getWeekDates(selectedDate));
    }, [selectedDate]);

    // ── Fetch data ──
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const getStartOfWeek = (date) => {
                    const d = new Date(date);
                    while (d.getDay() !== 1) d.setDate(d.getDate() - 1);
                    d.setDate(d.getDate() - 7);
                    return formatDateKey(d);
                };
                const getEndOfMonth = (date) => {
                    const d = new Date(date);
                    d.setMonth(d.getMonth() + 1);
                    d.setDate(0);
                    return formatDateKey(d);
                };

                const startDate = getStartOfWeek(new Date());
                const endDate = getEndOfMonth(new Date());
                const response = await axios.get(`/api/economic-calendar?from=${startDate}&to=${endDate}`);

                const organizedData = {};
                let latest = null;
                let earliest = null;

                response.data.data.forEach((item) => {
                    const date = formatDateKey(new Date(item.date));
                    if (!latest || date > latest) latest = date;
                    if (!earliest || date < earliest) earliest = date;
                    if (!organizedData[date]) organizedData[date] = [];
                    organizedData[date].push({ ...item, id: item._id });
                });

                setLatestDate(latest);
                setEarliestDate(earliest);
                setEconomicData(organizedData);
            } catch (error) {
                console.error('Error fetching economic data:', error);
            }
            setLoading(false);
        };
        fetchData();
    }, []);

    // ── Available countries ──
    const availableCountries = useMemo(() => {
        const countries = new Set();
        Object.values(economicData).forEach((dayEvents) => {
            dayEvents.forEach((e) => countries.add(e.country));
        });
        return [...countries].sort();
    }, [economicData]);

    // ── Filtered data ──
    const currentDayKey = formatDateKey(selectedDate);
    const rawDayData = useMemo(() => economicData[currentDayKey] || [], [economicData, currentDayKey]);

    const filteredData = useMemo(() => {
        let data = [...rawDayData];
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            data = data.filter((e) =>
                e.event.toLowerCase().includes(q) ||
                e.country.toLowerCase().includes(q) ||
                (e.currency && e.currency.toLowerCase().includes(q))
            );
        }
        if (selectedImpacts.length > 0) {
            data = data.filter((e) => selectedImpacts.includes(e.impact));
        }
        if (selectedCountries.length > 0) {
            data = data.filter((e) => selectedCountries.includes(e.country));
        }
        return data;
    }, [rawDayData, searchQuery, selectedImpacts, selectedCountries]);

    const hasActiveFilters = searchQuery.trim() !== '' || selectedImpacts.length > 0 || selectedCountries.length > 0;

    const clearFilters = () => {
        setSearchQuery('');
        setSelectedImpacts([]);
        setSelectedCountries([]);
    };

    // ── Navigation (fixed: no mutation of selectedDate) ──
    const handleDateNavigation = useCallback((direction) => {
        let newDate;
        if (isMobile) {
            newDate = getNextValidDate(selectedDate, direction);
        } else {
            newDate = new Date(selectedDate);
            newDate.setDate(newDate.getDate() + direction * 7);
        }
        if (latestDate && newDate > new Date(latestDate)) return;
        if (earliestDate && newDate < new Date(earliestDate)) return;
        setSelectedDate(newDate);
    }, [selectedDate, isMobile, latestDate, earliestDate]);

    const isNextDisabled = latestDate && new Date(selectedDate) >= new Date(latestDate);
    const isPrevDisabled = earliestDate && new Date(selectedDate) <= new Date(earliestDate);

    // ── Desktop DataGrid columns ──
    const columns = [
        {
            field: 'time',
            headerName: 'Time',
            minWidth: 130,
            flex: 0.8,
            valueGetter: (params) => formatTime(params.row.date),
            renderCell: (params) => {
                const isUpcoming = params.row.actual === null || params.row.actual === undefined;
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2">{formatTime(params.row.date)}</Typography>
                        {isUpcoming && <CountdownChip dateString={params.row.date} />}
                    </Box>
                );
            },
        },
        {
            field: 'impact',
            headerName: 'Impact',
            minWidth: 110,
            flex: 0.7,
            renderCell: (params) => <ImpactDots impact={params.value} theme={theme} />,
        },
        {
            field: 'event',
            headerName: 'Event',
            flex: 2,
            minWidth: 200,
            renderCell: (params) => {
                const isUpcoming = params.row.actual === null || params.row.actual === undefined;
                return (
                    <Typography variant="body2" sx={{
                        fontWeight: 500,
                        fontStyle: isUpcoming ? 'italic' : 'normal',
                        opacity: isUpcoming ? 0.7 : 1,
                    }}>
                        {params.value}
                        {isUpcoming && (
                            <Typography component="span" variant="caption"
                                sx={{ ml: 1, color: 'text.disabled' }}>(Pending)</Typography>
                        )}
                    </Typography>
                );
            },
        },
        {
            field: 'country',
            headerName: 'Country',
            minWidth: 110,
            flex: 0.7,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{params.value}</Typography>
                    <Typography variant="caption" color="text.secondary">{params.row.currency}</Typography>
                </Box>
            ),
        },
        {
            field: 'actual',
            headerName: 'Actual',
            minWidth: 90,
            flex: 0.6,
            renderCell: (params) => (
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatValue(params.value, params.row.unit)}
                </Typography>
            ),
        },
        {
            field: 'previous',
            headerName: 'Previous',
            minWidth: 90,
            flex: 0.6,
            renderCell: (params) => (
                <Typography variant="body2">{formatValue(params.value, params.row.unit)}</Typography>
            ),
        },
        {
            field: 'estimate',
            headerName: 'Estimate',
            minWidth: 90,
            flex: 0.6,
            renderCell: (params) => (
                <Typography variant="body2">{formatValue(params.value, params.row.unit)}</Typography>
            ),
        },
        {
            field: 'surprise',
            headerName: 'Surprise',
            minWidth: 110,
            flex: 0.7,
            sortComparator: (a, b) => {
                if (a === null || a === undefined) return 1;
                if (b === null || b === undefined) return -1;
                return Math.abs(b) - Math.abs(a);
            },
            valueGetter: (params) => getSurprise(params.row.actual, params.row.estimate),
            renderCell: (params) => (
                <SurpriseChip actual={params.row.actual} estimate={params.row.estimate}
                    unit={params.row.unit} theme={theme} />
            ),
        },
        {
            field: 'change',
            headerName: 'Change',
            minWidth: 120,
            flex: 0.8,
            renderCell: (params) => {
                if (params.value === null || params.value === undefined) {
                    return <Typography variant="body2">-</Typography>;
                }
                const isPositive = params.value > 0;
                return (
                    <Box sx={{
                        display: 'flex', alignItems: 'center',
                        color: isPositive ? theme.palette.success.main : theme.palette.error.main,
                    }}>
                        {isPositive
                            ? <TrendingUpIcon sx={{ fontSize: '1rem' }} />
                            : <TrendingDownIcon sx={{ fontSize: '1rem' }} />}
                        <Typography variant="body2" sx={{ ml: 0.5, fontWeight: 500 }}>
                            {isPositive ? '+' : ''}{params.value}
                            {params.row.changePercentage ? ` (${params.row.changePercentage}%)` : ''}
                        </Typography>
                    </Box>
                );
            },
        },
    ];

    // ── Loading state ──
    if (loading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mt: 8, gap: 2 }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">Loading economic events...</Typography>
            </Box>
        );
    }

    // ── Date navigation bar ──
    const renderDateNavigation = () => {
        if (isMobile) {
            return (
                <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    p: 1.5, borderBottom: 1, borderColor: 'divider',
                }}>
                    <IconButton onClick={() => handleDateNavigation(-1)} disabled={isPrevDisabled} size="small">
                        <ChevronLeftIcon />
                    </IconButton>
                    <Box onClick={() => setDatePickerOpen(true)}
                        sx={{ textAlign: 'center', cursor: 'pointer', '&:active': { opacity: 0.7 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                {formatMobileDate(selectedDate)}
                            </Typography>
                            <CalendarMonthIcon fontSize="small" color="primary" />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                            {rawDayData.length} Events{hasActiveFilters ? ` (${filteredData.length} shown)` : ''}
                        </Typography>
                        {isTodayDate(selectedDate) && (
                            <Chip label="Today" size="small" color="primary"
                                sx={{ ml: 1, height: 18, fontSize: '0.65rem', fontWeight: 700 }} />
                        )}
                    </Box>
                    <IconButton onClick={() => handleDateNavigation(1)} disabled={isNextDisabled} size="small">
                        <ChevronRightIcon />
                    </IconButton>
                </Box>
            );
        }

        // Desktop week bar
        return (
            <Box sx={{ display: 'flex', alignItems: 'stretch', borderBottom: 1, borderColor: 'divider' }}>
                <IconButton onClick={() => handleDateNavigation(-1)} disabled={isPrevDisabled}
                    sx={{ borderRadius: 0, px: 2, '&:hover': { backgroundColor: 'action.hover' }, opacity: isPrevDisabled ? 0.5 : 1 }}>
                    <ChevronLeftIcon />
                </IconButton>

                {weekDates.map((day) => {
                    const dayData = economicData[day.fullDate] || [];
                    const isSelected = formatDateKey(selectedDate) === day.fullDate;
                    const isToday = isTodayDate(day.date);

                    return (
                        <Box key={day.fullDate} onClick={() => setSelectedDate(day.date)}
                            sx={{
                                flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                                justifyContent: 'center', py: 1.5, cursor: 'pointer', position: 'relative',
                                backgroundColor: isSelected ? 'primary.main' : 'transparent',
                                transition: 'background-color 0.15s',
                                '&:hover': { backgroundColor: isSelected ? 'primary.dark' : 'action.hover' },
                            }}>
                            {/* Today indicator dot */}
                            {isToday && !isSelected && (
                                <Box sx={{
                                    position: 'absolute', top: 4, right: 4,
                                    width: 6, height: 6, borderRadius: '50%', backgroundColor: 'primary.main',
                                }} />
                            )}
                            <Typography sx={{
                                color: isSelected ? 'primary.contrastText' : 'text.primary',
                                fontWeight: isToday ? 700 : 500, fontSize: '0.875rem',
                            }}>
                                {day.formatted}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                <Typography variant="caption" sx={{
                                    color: isSelected ? 'primary.contrastText' : 'text.secondary',
                                    fontSize: '0.75rem',
                                }}>
                                    {dayData.length} Events
                                </Typography>
                                {isToday && (
                                    <Chip label="Today" size="small" sx={{
                                        height: 16, fontSize: '0.6rem', fontWeight: 700,
                                        backgroundColor: isSelected ? 'rgba(255,255,255,0.25)' : 'primary.main',
                                        color: isSelected ? 'primary.contrastText' : '#fff',
                                    }} />
                                )}
                            </Box>
                        </Box>
                    );
                })}

                {/* Calendar picker button */}
                <Tooltip title="Jump to date" arrow>
                    <IconButton onClick={() => setDatePickerOpen(true)}
                        sx={{
                            borderRadius: 0, px: 1.5, borderLeft: 1, borderColor: 'divider',
                            '&:hover': { backgroundColor: 'action.hover' },
                        }}>
                        <CalendarMonthIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                <IconButton onClick={() => handleDateNavigation(1)} disabled={isNextDisabled}
                    sx={{ borderRadius: 0, px: 2, '&:hover': { backgroundColor: 'action.hover' }, opacity: isNextDisabled ? 0.5 : 1 }}>
                    <ChevronRightIcon />
                </IconButton>
            </Box>
        );
    };

    // ── Main render ──
    return (
        <Container maxWidth="xl">
            <TitleAndInfo titleTop={titleTop} title={title} info={info} />

            <Paper elevation={3} variant="stats" sx={{ overflow: 'hidden', mb: 2 }}>
                {renderDateNavigation()}

                <FilterBar
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    selectedImpacts={selectedImpacts}
                    setSelectedImpacts={setSelectedImpacts}
                    selectedCountries={selectedCountries}
                    setSelectedCountries={setSelectedCountries}
                    availableCountries={availableCountries}
                    onClearFilters={clearFilters}
                    hasActiveFilters={hasActiveFilters}
                    isMobile={isMobile}
                    theme={theme}
                />
            </Paper>

            {/* Content area */}
            {filteredData.length === 0 ? (
                <EmptyState
                    message={rawDayData.length === 0
                        ? 'No events scheduled for this day'
                        : 'No events match your filters'}
                />
            ) : isMobile ? (
                <Box sx={{ pb: 4 }}>
                    {[...filteredData]
                        .sort((a, b) => new Date(a.date) - new Date(b.date))
                        .map((event) => (
                            <MobileEventCard key={event.id} event={event} theme={theme} />
                        ))}
                </Box>
            ) : (
                <Box sx={{ height: 650, width: '100%' }}>
                    <DataGrid
                        rows={filteredData}
                        columns={columns}
                        disableColumnMenu
                        paginationPosition="middle"
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10 } },
                            sorting: { sortModel: [{ field: 'time', sort: 'asc' }] },
                        }}
                        pageSizeOptions={[5, 10, 25]}
                        getRowClassName={(params) => {
                            const isUpcoming = params.row.actual === null || params.row.actual === undefined;
                            return isUpcoming ? 'upcoming-row' : '';
                        }}
                        sx={{
                            '& .upcoming-row': { opacity: 0.65, fontStyle: 'italic' },
                            '& .MuiDataGrid-row:hover': { backgroundColor: theme.palette.action.hover },
                        }}
                    />
                </Box>
            )}

            {/* Date Picker Dialog */}
            <DatePickerDialog
                open={datePickerOpen}
                onClose={() => setDatePickerOpen(false)}
                onSelect={(date) => setSelectedDate(date)}
                selectedDate={selectedDate}
                earliestDate={earliestDate}
                latestDate={latestDate}
                theme={theme}
            />
        </Container>
    );
};

export default EconomicCalendar;